// Who may do what's role dialogs, on FormDialog: copying a role and making one from nothing (the owner's).
// Each asks the owner's fresh second factor first when the server wants it (QA-685), the code box over
// the dialog. Blueprint's RolesPage.tsx and Riverstone's matrix (Settings.tsx) both open them.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { FormDialog, TextField } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { useStepUp } from '../shared/StepUpAsk.tsx';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

/** Clone a role: an editable copy with its own name (roles.clone, the owner's), on a FormDialog. */
export function CloneDialog({
  from,
  onClose,
  onDone,
}: {
  from: { key: string; name: string };
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [name, setName] = useState(`${from.name} (copy)`);
  const { run, ask } = useStepUp();
  return (
    <>
      <FormDialog
        isOpen
        title={`Clone the ${from.name} role`}
        submitLabel="Clone"
        isDirty={false}
        isSubmitDisabled={!name.trim()}
        onClose={onClose}
        onSubmit={async () => {
          await run(() => client.call('roles.clone', { fromKey: from.key, name: name.trim() })).catch(
            (err: unknown) => {
              throw new Error(message(err));
            },
          );
          await onDone();
        }}
      >
        <TextField
          label="Name"
          description={`The copy starts with the same permissions and is yours to change; ${from.name} stays as it is.`}
          value={name}
          onChange={setName}
        />
      </FormDialog>
      {ask}
    </>
  );
}

/** A new role from nothing (roles.create, the owner's): a name now, its permissions in the matrix. */
export function NewRoleDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const [name, setName] = useState('');
  const { run, ask } = useStepUp();
  return (
    <>
      <FormDialog
        isOpen
        title="New role"
        submitLabel="Make the role"
        isDirty={false}
        isSubmitDisabled={!name.trim()}
        onClose={onClose}
        onSubmit={async () => {
          await run(() => client.call('roles.create', { name: name.trim(), permissions: [] })).catch(
            (err: unknown) => {
              throw new Error(message(err));
            },
          );
          await onDone();
        }}
      >
        <TextField
          label="Name"
          description="It starts with nothing: tick what it allows in the full list. Give it to people beside their other roles, such as a Margins role beside Customer service."
          value={name}
          onChange={setName}
        />
      </FormDialog>
      {ask}
    </>
  );
}
